import { Grid, Tooltip } from '@mui/material';
import React from 'react';
import { ImportContext, ImportContextProps } from '../ImportDataButton';
import { MuiFileInput } from 'mui-file-input';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/Download.svg';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { downloadImportSitesFromCsvTemplate } from '../../../util/csv';
import { UserContext } from '../../../context/UserContext';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

// Data sent in request to unique Cloud Function.
interface ISiteDataImportContext extends ImportContext {
  source: string;
  group_id: string;
  company_id: string;
  sites: File | null;
}

const SOURCE = 'SiteDataImportContext.tsx';

/**
 * Reusable importing context for configured site file data containing relevant inputs for the microservice.
 * @param {void} handleContextChange Update context in parent from input changes.
 * @param {void} handleContextValid Update context validity in parent based on input changes.
 * @returns {JSX.Element}
 */
const SiteDataImportContext = ({ handleContextChange, handleContextValid }: ImportContextProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [loadingTemplate, setLoadingTemplate] = React.useState<boolean>(false);

  // Default context state for inputs.
  const initial_context: ISiteDataImportContext = {
    source: '',
    group_id: group?.id ?? '',
    company_id: company?.id ?? '',
    sites: null
  };
  const [siteDataImportContext, setSiteDataImportContext] =
    React.useState<ISiteDataImportContext>(initial_context);

  // Validation check for unique import context.
  const context_valid = Boolean(
    siteDataImportContext.group_id &&
    siteDataImportContext.company_id &&
    siteDataImportContext.sites &&
    user &&
    user.super_admin
  );

  // Update validity of context in parent whenever valid check value changes.
  React.useEffect(() => {
    handleContextValid(context_valid);
  }, [context_valid, handleContextValid]);

  const handleFileChange = (new_file: File | null): void => {
    const updated_context: ISiteDataImportContext = {
      ...siteDataImportContext,
      sites: new_file,
      source: new_file?.name ?? ''
    };
    setSiteDataImportContext(updated_context);
    handleContextChange(updated_context);
  };

  // Download File Import template for current company and reporting period.
  const downloadTemplateHandler = async (): Promise<void> => {
    try {
      setLoadingTemplate(true);
      if (!group || !company) {
        throw new Error('Missing group and company data required by template download.');
      }
      await downloadImportSitesFromCsvTemplate(group.id, company.id);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'downloadTemplateHandler'),
          {
            siteDataImportContext: siteDataImportContext
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch context entities', tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingTemplate(false);
    }
  };

  React.useEffect(() => {
    (async () => {
      setSiteDataImportContext(initial_context);
    })();
  }, [user, company, group]);

  return (
    <>
      <Grid size={'auto'} sx={{ minWidth: 250, display: 'flex', flexDirection: 'column' }}>
        <MuiFileInput
          value={siteDataImportContext.sites}
          onChange={handleFileChange}
          title="Choose Data File"
          label="Data File"
          inputProps={{ accept: '.csv' }}
        />
        <Tooltip title="Download Template">
          <span style={{ display: 'block', marginTop: 12, width: 'fit-content' }}>
            <PrimaryButton
              loading={loadingTemplate}
              onClick={downloadTemplateHandler}
              disabled={!siteDataImportContext.company_id}
              startIcon={<DownloadIcon height={25} />}
              sx={{ mr: 'auto' }}
            >
              Download Template
            </PrimaryButton>
          </span>
        </Tooltip>
      </Grid>
    </>
  );
};

export default SiteDataImportContext;
